/**
 * `customers.*` — buyer master CRUD + archive.
 *
 * Architecture: domain functions `createCustomer / updateCustomer /
 * archiveCustomer / getCustomer / listCustomers` are called by the matching
 * tRPC procedures. Each procedure does parse → call domain → map errors
 * → return result (architecture §6.1).
 *
 * Customer is master data (mutable; UPDATE allowed by RLS) — contrast
 * `invoices` which is INSERT-only. Hard delete is impossible because
 * `invoices.customer_id` and `receipts.customer_id` are `onDelete:'restrict'`;
 * archive is the soft-delete seam (ADR-0007).
 */
import { z } from "zod";
import {
  uuid,
  paged,
  cursor,
  gstin,
  phoneIN,
  pin,
  indianState,
  isoDate,
  moneyPaise,
  signedPaise,
} from "../_common.js";

/**
 * IT Act sections under which a customer may deduct TDS when paying us.
 *
 * Most-common B2B SME paths:
 *   - **194C** — payment to contractors (1% individuals / 2% non-individuals)
 *   - **194J** — fees for professional / technical services (10%)
 *   - **194Q** — purchase of goods over ₹50L threshold (0.1%)
 *
 * Other sections (194-IA, 194-IB, etc.) aren't surfaced — they apply to
 * non-customer flows. Adding a new section means: this enum + the SPA
 * picker. (PR-B.)
 */
export const tdsSection = z.enum(["194C", "194J", "194Q"]);

/**
 * Default tax-treatment label for new invoices to this buyer. CGST+SGST or
 * IGST is auto-derivable from `state`; the column exists so `zero_rated`
 * (export / SEZ) and `exempt` are persistable customer-classification
 * overrides. The DB layer locks the enum via a CHECK constraint
 * (migration 0030). Adding a new mode means: this enum + CHECK +
 * domain mapping. (Phase C.)
 */
export const customerTaxMode = z.enum(["cgst_sgst", "igst", "zero_rated", "exempt"]);

/**
 * Default delivery channel for invoices + reminders. WhatsApp is the
 * V1 default for Indian SMEs; reminders fall back to email if WhatsApp
 * delivery fails. SMS is OTP-only per system-decisions §6. The CHECK
 * constraint in migration 0031 mirrors this enum.
 */
export const customerChannel = z.enum(["whatsapp", "email", "sms", "print"]);

/**
 * The patchable fields on a customer master row. Reused by `createInput`
 * (all required-or-optional based on the domain) and `updateInput` (all
 * optional via `.partial()` so the caller can patch any subset).
 *
 * Why share the shape: the wire schema and the form schema collapse to one
 * source of truth; if a new field lands later, it lands here once.
 */
const baseFields = z.object({
  /** Display name on the buyer side. The only required field. */
  name: z.string().trim().min(1, "Name is required").max(255),
  contactName: z.string().trim().min(1).max(255).optional(),
  gstin: gstin.optional(),
  phone: phoneIN.optional(),
  /**
   * Secondary contact phone — same shape as `phone`. Empty string on
   * update clears the column. (Migration 0032.)
   */
  phoneSecondary: phoneIN.optional(),
  /**
   * WhatsApp endpoint marker — false (default) = primary phone, true
   * = secondary phone. The SPA disables this when secondary is blank.
   * (Migration 0032.)
   */
  whatsappOnSecondary: z.boolean().optional(),
  email: z.string().email().optional(),
  addressLine1: z.string().trim().min(1).max(255).optional(),
  addressLine2: z.string().trim().min(1).max(255).optional(),
  state: indianState.optional(),
  city: z.string().trim().min(1).max(64).optional(),
  pincode: pin.optional(),
  /**
   * Country (ISO name). Null/absent = domestic (India-assumed). Set for
   * export / overseas buyers — captured on the composer's Export pane and
   * editable via the address editor. (Migration 0058.)
   */
  country: z.string().trim().min(1).max(100).optional(),
  /**
   * Payment terms label. The SPA's customer form writes structured strings
   * via the Net-N picker (`'Net 0' | 'Net 7' | 'Net 15' | 'Net 30' | 'Net 45'
   * | 'Net 60' | 'Net 90'`) so the composer's `parseTermDays` extracts the
   * day count reliably. The column stays free-form (varchar(32)) so a Tally
   * import can land arbitrary historical labels — the contract just enforces
   * the length cap, not the format. (PR-A.8.)
   */
  paymentTerms: z.string().trim().max(32).optional(),
  /**
   * Credit limit in paise (stringified bigint on the wire — see `moneyPaise`).
   * Optional. Null on the row = no limit set; the composer's RiskCard renders
   * "No limit set". Outstanding-vs-limit math arrives with the receipts
   * vertical (V1.x); for now the customer form persists this column so the
   * RiskCard can at least show the operator's intent. (PR-A.8.)
   */
  creditLimitPaise: moneyPaise.optional(),
  /**
   * Opening balance — what this customer already owed us at
   * `openingBalanceAsOf`. Migration enabler: on day 1, operators key in
   * the prior balance from Tally so the receipts vertical can reconcile
   * against it. Empty string on update clears. (PR-B.)
   */
  openingBalancePaise: moneyPaise.optional(),
  openingBalanceAsOf: isoDate.optional(),
  /**
   * Whether this customer deducts TDS when paying us. The receipts
   * vertical (PR-C) defaults the TDS-deduction line on a recorded
   * receipt based on this flag + `tdsSection`. (PR-B.)
   */
  isTdsApplicable: z.boolean().optional(),
  /**
   * Section under which the customer deducts. Required when
   * `isTdsApplicable=true`; ignored otherwise. The domain doesn't
   * enforce the cross-field requirement (it's a UX nicety, not a data
   * invariant) — the SPA's form schema does. `null` is the explicit
   * "clear this column" sentinel on update; `undefined` means "skip".
   * (PR-B.)
   */
  tdsSection: tdsSection.nullable().optional(),
  // ── Phase C (migration 0030) ──────────────────────────────────────
  /**
   * Operator-only filter labels. Sent as an array; the domain replaces the
   * stored set wholesale (no add/remove granularity yet). Empty array
   * clears all tags. Per-tag length cap 40, max 20 tags per customer to
   * keep the row + list-payload small. (Phase C.)
   */
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  /**
   * Pinned internal note. Empty string clears the column. 2000-char cap
   * matches the invoice-notes pattern from 0011. (Phase C.)
   */
  internalNote: z.string().trim().max(2000).optional(),
  /**
   * Default tax mode. Auto-derivable from `state` for cgst_sgst/igst;
   * persisted so zero_rated / exempt customers round-trip. (Phase C.)
   */
  taxMode: customerTaxMode.optional(),
  /**
   * Default HSN/SAC pre-fill for the composer. Empty string clears.
   * 4-8 digits — we only enforce the length cap, not the format
   * (HSN codes vary by goods/services classification). (Phase C.)
   */
  defaultHsn: z.string().trim().max(8).optional(),
  /**
   * Default GST percentage (0/5/12/18/28 are the common rates;
   * we accept any 0-100 integer for forward-compat with rate changes).
   * `null` clears, `undefined` skips. (Phase C.)
   */
  defaultGstRatePct: z.number().int().min(0).max(100).nullable().optional(),
  /** E-invoice (NIC IRP) generation required for this buyer. (Phase C.) */
  eInvoiceRequired: z.boolean().optional(),
  /** E-way bill required on dispatch. (Phase C.) */
  eWayBillRequired: z.boolean().optional(),
  /** Buyer segment label (Retail / Wholesale / etc.). Empty string clears. (Phase C.) */
  customerCategory: z.string().trim().max(64).optional(),
  /** Default revenue GL pre-fill. Empty string clears. (Phase C.) */
  defaultRevenueGl: z.string().trim().max(64).optional(),
  /** Early-payment clause printed on the invoice. Empty string clears. (Phase C.) */
  earlyDiscount: z.string().trim().max(64).optional(),
  // ── Phase C cont. — communication prefs (migration 0031) ──────────
  /** Default delivery channel. Never null — column has NOT NULL default 'whatsapp'. */
  defaultChannel: customerChannel.optional(),
  remind7dBefore: z.boolean().optional(),
  remind1dBefore: z.boolean().optional(),
  remindOnDue: z.boolean().optional(),
  remind7dAfter: z.boolean().optional(),
  monthlyStatement: z.boolean().optional(),
  marketingOptIn: z.boolean().optional(),
});

// ── create ────────────────────────────────────────────────────────
export const createInput = baseFields;
export const createOutput = z.object({ id: uuid });

// ── import (batch create from CSV) ────────────────────────────────
/**
 * One CSV row = one customer create payload. We omit fields the CSV
 * doesn't collect in V1 (tags / internalNote / earlyDiscount) — those
 * remain add-later-through-the-UI. Everything else in `createInput`
 * (name / GSTIN / state / PIN / opening balance / etc.) still applies,
 * so the same Zod validation catches broken rows early.
 */
export const importRow = createInput.omit({
  tags: true,
  internalNote: true,
  earlyDiscount: true,
});

export const importInput = z.object({
  /** At least one, at most 500 rows per batch — V1 sync cap. */
  rows: z.array(importRow).min(1).max(500),
});

export const importOutput = z.object({
  imported: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      /** 0-based index into `importInput.rows`. */
      rowIndex: z.number().int().nonnegative(),
      reason: z.enum(["duplicate_gstin", "validation", "internal_error"]),
      /** Human-readable summary for the toast / detail sheet. */
      message: z.string(),
    }),
  ),
});

// ── get one ───────────────────────────────────────────────────────
export const getInput = z.object({ id: uuid });

/**
 * Full customer row as returned to the SPA. Optional master fields are
 * `nullable()` (DB-shape) on the wire — the form converts to `undefined`
 * before round-tripping; the DB stores NULLs.
 */
export const customerRow = z.object({
  id: uuid,
  name: z.string(),
  contactName: z.string().nullable(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  /** Secondary phone — null when not set. (Migration 0032.) */
  phoneSecondary: z.string().nullable(),
  /** Whether WhatsApp targets the secondary slot. (Migration 0032.) */
  whatsappOnSecondary: z.boolean(),
  email: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  pincode: z.string().nullable(),
  /** Country (ISO name). Null = domestic / India-assumed. (Migration 0058.) */
  country: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  /** Stringified bigint of paise. Null = no limit set. (PR-A.8.) */
  creditLimitPaise: z.string().nullable(),
  /** Stringified bigint of paise. Null = no opening balance. (PR-B.) */
  openingBalancePaise: z.string().nullable(),
  openingBalanceAsOf: z.string().nullable(),
  isTdsApplicable: z.boolean(),
  tdsSection: z.string().nullable(),
  /** Operator-only filter labels. Always an array (never null). (Phase C.) */
  tags: z.array(z.string()),
  /** Pinned internal note. (Phase C.) */
  internalNote: z.string().nullable(),
  /** Default tax-treatment override. Never null — column has NOT NULL default 'cgst_sgst'. (Phase C.) */
  taxMode: customerTaxMode,
  defaultHsn: z.string().nullable(),
  defaultGstRatePct: z.number().int().nullable(),
  eInvoiceRequired: z.boolean(),
  eWayBillRequired: z.boolean(),
  customerCategory: z.string().nullable(),
  defaultRevenueGl: z.string().nullable(),
  earlyDiscount: z.string().nullable(),
  /** Communication prefs — never null (NOT NULL defaults). (Migration 0031.) */
  defaultChannel: customerChannel,
  remind7dBefore: z.boolean(),
  remind1dBefore: z.boolean(),
  remindOnDue: z.boolean(),
  remind7dAfter: z.boolean(),
  monthlyStatement: z.boolean(),
  marketingOptIn: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const getOutput = customerRow;

// ── update ────────────────────────────────────────────────────────
/**
 * Patch fields are all optional (`.partial()`); `id` selects the row.
 * The domain rejects an empty patch — there's nothing to write — so the
 * router's `BAD_REQUEST` mapping kicks in if a UI bug submits one.
 *
 * Empty-string on regex/min-1 string fields rounds-trips to null (matches
 * the companies-updateInput convention and the customer domain, which
 * treats `""` as "clear to null" in `updateCustomer`'s diff loop). Without
 * these unions, the create-time regex (`phoneIN`, `gstin`, `pin`, `.email()`)
 * or `.min(1)` guard fires on the wire before the domain ever sees the
 * patch, so a cleared secondary phone / email / address line can't be
 * saved from the edit dialogs.
 */
export const updateInput = baseFields
  .partial()
  .extend({
    id: uuid,
    contactName: z.union([z.string().trim().min(1).max(255), z.literal("")]).optional(),
    gstin: z.union([gstin, z.literal("")]).optional(),
    phone: z.union([phoneIN, z.literal("")]).optional(),
    phoneSecondary: z.union([phoneIN, z.literal("")]).optional(),
    email: z.union([z.string().email(), z.literal("")]).optional(),
    addressLine1: z.union([z.string().trim().min(1).max(255), z.literal("")]).optional(),
    addressLine2: z.union([z.string().trim().min(1).max(255), z.literal("")]).optional(),
    state: z.union([indianState, z.literal("")]).optional(),
    city: z.union([z.string().trim().min(1).max(64), z.literal("")]).optional(),
    pincode: z.union([pin, z.literal("")]).optional(),
    country: z.union([z.string().trim().min(1).max(100), z.literal("")]).optional(),
  });
export const updateOutput = z.object({ id: uuid });

// ── activity (audit timeline) ─────────────────────────────────────
/**
 * Per-customer audit history. The customer-detail overview merges these
 * with invoice events so every edit on the record (created / updated /
 * archived) shows up on the timeline alongside transactional events.
 */
export const activityInput = z.object({
  id: uuid,
  /** Page size — default 10 matches the "Load more" cadence on the SPA. */
  limit: z.number().int().min(1).max(100).default(10),
  /**
   * Opaque cursor — encoded as `${at-iso}|${row-id}`. `undefined` returns
   * the first (newest) page; subsequent pages echo the `nextCursor` from
   * the previous response.
   */
  cursor,
});

export const activityRow = z.object({
  id: uuid,
  /** `customer.created` / `customer.updated` / `customer.archived`. */
  verb: z.string(),
  /** `web` / `whatsapp` / `api` / `system` / `ai`. */
  // Same union as `auditActorType` — these rows come straight from
  // `audit_entries`, so an actor kind this enum omits makes the procedure
  // reject its own data rather than render it.
  actorType: z.enum(["web", "whatsapp", "api", "system", "ai", "buyer"]),
  /** User id or service identifier; the SPA resolves a human name later. */
  actorId: z.string(),
  /**
   * Human-readable actor name resolved server-side from `users.name` for
   * `actorType='web'`. Null for non-web actors (system / ai / api /
   * whatsapp) — the SPA labels those generically.
   */
  actorName: z.string().nullable(),
  /** Membership role at the time of the audit (Founder / Operator / etc.). */
  actorRole: z.string().nullable(),
  /**
   * Free-form metadata as recorded by the domain. For `updated` rows it
   * contains `{ changedKeys, before, after }`; the SPA reads
   * `changedKeys` to render the friendly summary line.
   */
  metadata: z.record(z.string(), z.unknown()),
  at: z.string().datetime(),
});

export const activityOutput = paged(activityRow);

// ── ledger ────────────────────────────────────────────────────────
/**
 * Read-only chronological ledger for one customer. Unions this
 * customer's invoices, receipts, and credit notes into a running-
 * balance table. Powers the `/customers/:id/ledger` page (V1.0.x
 * minimum viable — no filters, no export). Running balance is
 * signed paise: positive = customer owes us, negative = customer
 * has advance / credit.
 */
export const ledgerInput = z.object({ id: uuid });

export const ledgerRow = z.object({
  kind: z.enum(["invoice", "receipt", "credit_note"]),
  entryId: uuid,
  at: isoDate,
  /** Doc number when present ("" for draft invoices, receipts). */
  docNumber: z.string(),
  /** Positive stringified paise. "0" if this row is credit-side. */
  debitPaise: moneyPaise,
  /** Positive stringified paise. "0" if this row is debit-side. */
  creditPaise: moneyPaise,
  /** Signed stringified paise. Negative = customer credit / advance. */
  runningBalancePaise: signedPaise,
  /** Invoice status ("paid"/"issued"/"void"/etc); null for non-invoices. */
  status: z.string().nullable(),
});

export const ledgerOutput = z.object({
  /** Opening balance from `customers.opening_balance_paise`. Signed paise. */
  openingBalancePaise: signedPaise,
  /** Balance after all rows applied. Signed paise. */
  closingBalancePaise: signedPaise,
  rows: z.array(ledgerRow),
});

// ── archive ───────────────────────────────────────────────────────
export const archiveInput = z.object({
  id: uuid,
  /** Optional human-readable reason — lands on the audit row's metadata. */
  reason: z.string().trim().max(255).optional(),
});
export const archiveOutput = z.object({
  id: uuid,
  archivedAt: z.string().datetime(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  /** Free-text prefix search across name, GSTIN, city, and contact name.
   *  Case-insensitive. Trimmed. */
  q: z.string().optional(),
  /**
   * `'all'` (default) excludes archived. `'archived'` shows only archived.
   * `'overdue'`, `'active'`, `'new'` narrow the active set by invoice/created
   * signals (see domain WHERE clauses). Server-side filters — the client used
   * to narrow the current page, which silently hid rows on other pages.
   */
  tab: z.enum(["all", "archived", "overdue", "active", "new"]).default("all"),
});

/**
 * One row in the list response. Keep this lean — list views fetch many,
 * detail views fetch one. `email`/`addressLine*`/`pincode` aren't here
 * because the prototype's list (S09) doesn't render them.
 */
export const listRow = z.object({
  id: uuid,
  name: z.string(),
  contactName: z.string().nullable(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  pincode: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  /** Stringified bigint paise. Surfaced on listRow so the invoice composer's
   *  customer picker can hand the RiskCard a credit limit without a second
   *  fetch. (PR-A.8.) */
  creditLimitPaise: z.string().nullable(),
  /** Open invoice balance after receipts and credit notes. */
  outstandingPaise: moneyPaise,
  /** Financial-year invoiced value. */
  ytdInvoicedPaise: moneyPaise,
  /** Count of issued/non-void invoices in the current financial year. */
  ytdInvoiceCount: z.number().int().nonnegative(),
  /** Count of issued/non-void invoices on record. */
  invoiceCount: z.number().int().nonnegative(),
  /** Active invoices past due date. */
  overdueInvoiceCount: z.number().int().nonnegative(),
  /** Latest invoice activity timestamp, if any. */
  lastActivityAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── export (CSV download) ────────────────────────────────────────
export const exportInput = z.object({
  q: z.string().optional(),
  tab: z.enum(["all", "archived", "overdue", "active", "new"]).default("all"),
  sort: z.enum(["recent", "name", "exposure"]).default("recent"),
});

export const exportOutput = z.object({
  /** `customers-<tab>-YYYY-MM-DD.xlsx`. */
  fileName: z.string(),
  /** Raw row set — the client builds the workbook with ExcelJS, mirroring
   *  the customer-statement pattern at `apps/web/src/features/customers/ledger/`.
   *  Same shape as `customers.list` rows. */
  rows: z.array(listRow),
  /** Data rows returned (excludes header). */
  rowCount: z.number().int().nonnegative(),
  /** True when the 5000-row cap was hit and rows beyond it were dropped. */
  truncated: z.boolean(),
});

// ── derived TS types (consumed by domain + web) ───────────────────
export type CreateInput = z.infer<typeof createInput>;
export type ExportInput = z.infer<typeof exportInput>;
export type ImportInput = z.infer<typeof importInput>;
export type ImportOutput = z.infer<typeof importOutput>;
export type ImportRow = z.infer<typeof importRow>;
export type ExportOutput = z.infer<typeof exportOutput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type ArchiveInput = z.infer<typeof archiveInput>;
export type ArchiveOutput = z.infer<typeof archiveOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type CustomerRow = z.infer<typeof customerRow>;
export type ActivityInput = z.infer<typeof activityInput>;
export type ActivityOutput = z.infer<typeof activityOutput>;
export type ActivityRow = z.infer<typeof activityRow>;
export type LedgerInput = z.infer<typeof ledgerInput>;
export type LedgerOutput = z.infer<typeof ledgerOutput>;
export type LedgerRow = z.infer<typeof ledgerRow>;
